import { createColumnHelper } from '@tanstack/react-table';
import { array } from 'fp-ts';
import { PortfolioPropertyRateGroupCount } from '@mews-commander/data-access';
import { LocalizedText, lt } from '@mews/framework';
import {
    IconName,
    Table,
    TableBulkActionButton,
    TableBulkActions,
    TableHeader,
    TableProvider,
    useRowSelection,
    useTableState,
} from '@optimus-web/core';
import { useDeleteConfirmationDialog } from '@mews-commander/ui';
import { TextKey } from '@mews-commander/enums';
import { PolicyData } from '../types';
import { useAssignPropertyContext } from '../contexts';
import { RemovePropertyButton } from './RemovePropertyButton';

const columnHelper = createColumnHelper<PortfolioPropertyRateGroupCount>();

const columns = [
    columnHelper.accessor('name', {
        header: () => lt('Name'),
        cell: (props) => props.getValue(),
    }),
    columnHelper.accessor('rateGroupCount', {
        header: () => lt('RateGroupPlural'),
        cell: (props) => props.getValue(),
    }),
    columnHelper.display({
        id: 'actions',
        meta: { align: 'end' },
        cell: (props) => <RemovePropertyButton enterpriseId={props.row.original.id} />,
    }),
];

interface PropertiesSectionProps {
    data: PolicyData;
}

export const PropertiesSection = ({ data: { propertyRateGroupCounts } }: PropertiesSectionProps) => {
    const [tableState, setTableState] = useTableState<PortfolioPropertyRateGroupCount>();
    const { rowSelection, setRowSelection, resetRowSelection, selectedRowsIds, hasRowsSelected } = useRowSelection();

    const propertyRows =
        propertyRateGroupCounts?.filter(({ name }) => name.includes(tableState.globalFilters.search ?? '')) ?? [];
    const propertyCount = propertyRows.length;

    const { unassignProperties } = useAssignPropertyContext();
    const { confirmationDialog, openConfirmationDialog } = useDeleteConfirmationDialog({
        deleteAction: async () => unassignProperties({ enterpriseIds: selectedRowsIds }).then(resetRowSelection),
        renderContent: () => lt('RemovePolicyFromPropertyDescription'),
        title: (
            <LocalizedText
                textKey={TextKey.RemovePolicyFromPropertyTitle}
                substitutes={{ count: selectedRowsIds.length }}
            />
        ),
    });

    return (
        <TableProvider columns={columns} state={tableState} onStateChange={setTableState} as="div">
            <TableHeader
                title={lt('Properties')}
                subTitle={propertyCount}
                globalSearch={{
                    placeholder: lt('PropertiesSearchHint'),
                    debounce: false,
                    maxWidth: '240px',
                }}
                onRowSelectionChange={setRowSelection}
            />
            <TableBulkActions
                disabled={!hasRowsSelected}
                leftSide={
                    array.isNonEmpty(selectedRowsIds) ? lt('SelectedItems', { count: selectedRowsIds.length }) : null
                }
                rightSide={
                    <TableBulkActionButton
                        icon={IconName.Delete}
                        disabled={!hasRowsSelected}
                        size="small"
                        onClick={openConfirmationDialog}
                    >
                        {lt('Remove')}
                    </TableBulkActionButton>
                }
            />
            <Table
                data={propertyRows}
                amountOfRows={propertyCount}
                rowSelection={{ rowSelection, onRowSelectionChange: setRowSelection }}
                uniqueRowId="id"
            />
            {confirmationDialog}
        </TableProvider>
    );
};
